import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ACTIONS from "../../../../redux/action";
import axios from "../../../../shared/axios/axiosProxy";
import i18n from "../../../../utils/i18n";
import classes from "./companyForeignTrade.module.scss";

/** Industries that show the foreign trade step (others skip to CompanyContactPage). */
export const COMPANY_INDUSTRIES_WITH_FOREIGN_TRADE = [
  "etrade",
  "construction",
  "retail",
  "wholesale",
  "art",
  "beauty",
  "logistics",
  "manufacturing",
  "finance",
  "startup",
  "vehicle",
];

export const companyIndustryHasForeignTradeStep = (industry) =>
  COMPANY_INDUSTRIES_WITH_FOREIGN_TRADE.includes(industry);

const COMPANY_FOREIGN_TRADE_OPTIONS = [
  { value: "yes", label: i18n.t("general.yes"), emoji: "✅" },
  { value: "none", label: i18n.t("general.no"), emoji: "❌" },
];

const getInitialRadioValue = (foreign_trade, foreigntradeEU) => {
  if (foreign_trade === true && foreigntradeEU === "outside") {
    return "yes";
  }
  if (foreign_trade === false) {
    return "none";
  }
  return "";
};

export const isCompanyForeignTradeAnswered = (foreign_trade, foreigntradeEU) =>
  getInitialRadioValue(foreign_trade, foreigntradeEU) !== "";

const Radio = ({ checked }) => (
  <span
    className={`${classes.radio} ${checked ? classes.radioChecked : ""}`}
    aria-hidden="true"
  />
);

const CompanyForeignTrade = () => {
  const companyStore = useSelector((state) => state.company_store);
  const dispatch = useDispatch();
  const [radioValue, setRadioValue] = useState(() =>
    getInitialRadioValue(companyStore.foreign_trade, companyStore.foreigntradeEU),
  );

  const syncSearchForeignTradeFields = useCallback((searchID, foreign_trade, foreigntradeEU) => {
    if (!searchID) {
      return;
    }
    axios
      .patch(
        `/api/search/${searchID}`,
        { foreign_trade, foreigntradeEU },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      )
      .catch((error) => {
        console.error(
          "Error saving foreign trade fields to searchSchema:",
          error.response?.data || error.message,
        );
      });
  }, []);

  const applyForeignTradeChoice = useCallback(
    (choice) => {
      let foreign_trade;
      let foreigntradeEU;

      if (choice === "none") {
        foreign_trade = false;
        foreigntradeEU = "";
      } else if (choice === "yes") {
        foreign_trade = true;
        foreigntradeEU = "outside";
      } else {
        return;
      }

      dispatch(
        ACTIONS.addCompanyInfo({
          id: "foreign_trade",
          data: foreign_trade,
          stateObject: "company_store",
        }),
      );
      dispatch(
        ACTIONS.addCompanyInfo({
          id: "foreigntradeEU",
          data: foreigntradeEU,
          stateObject: "company_store",
        }),
      );
      syncSearchForeignTradeFields(companyStore.searchID, foreign_trade, foreigntradeEU);
    },
    [companyStore.searchID, dispatch, syncSearchForeignTradeFields],
  );

  const handleForeignTradeSelect = useCallback(
    (choice) => {
      setRadioValue(choice);
      applyForeignTradeChoice(choice);
    },
    [applyForeignTradeChoice],
  );

  return (
    <div className={classes.root}>
      <div className={classes.scrollArea}>
        <div className={classes.content}>
          <div className={classes.mainColumn}>
            <header className={classes.header}>
              <h1 className={classes.title}>{i18n.t("searchflow.foreigntradetitle")}</h1>
              <p className={classes.subtitle}>
                {i18n.t("searchflow.foreigntradesubtitle")}
              </p>
            </header>

            <div className={classes.rightColumn}>
              <div className={classes.cardsContainer}>
                {COMPANY_FOREIGN_TRADE_OPTIONS.map((option) => {
                  const isSelected = radioValue === option.value;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      className={`${classes.card} ${isSelected ? classes.selectedRow : ""}`}
                      onClick={() => handleForeignTradeSelect(option.value)}
                      aria-pressed={isSelected}
                    >
                      <span className={classes.cardEmoji} aria-hidden="true">
                        {option.emoji}
                      </span>
                      <div className={classes.cardContent}>
                        <div className={classes.cardTitle}>{option.label}</div>
                      </div>
                      <Radio checked={isSelected} />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyForeignTrade;
